import React, { useCallback, useEffect, useRef, useState } from "react";
import GoogleMapView from "../../../shared/components/GoogleMap/GoogleMapView";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import {
  defaultCountry,
  defaultDropDownValue,
  Directions,
  translationConstants,
} from "../../../constants";
import SpotAttributeWrapper from "../../../shared/components/PublicSpot/SpotAttributeWrapper";
import { useDispatch, useSelector } from "react-redux";
import { getSpotTypes } from "./utlilites";
import { SpotActions, authActions } from "../../../redux/actions";
import InfiniteScroll from "react-infinite-scroll-component";
import { loadingIndicator, dataEndMessage } from "../../../shared/utilitiies";
import useFetchCoordinatesByCity from "../../../shared/hooks/useFetchCordinatesByCity";

function PublicSpot() {
  const defaultQueryExpression = `id != null`;
  const filterKeyCityId = "CityId";
  const filterKeySpotType = "SpotId";
  const [cityFilter, setCityFilterValue] = useState("");
  const initialGridState = {
    length: 10,
    search: null,
    pageNumber: 1,
    orderBy: "id desc",
    filterQuery: defaultQueryExpression,
  };
  const { t, i18n } = useTranslation();
  const [spotTypes, setSpotTypes] = useState(getSpotTypes(t));
  const [mainMapLocations, setMainMapLocations] = useState("");
  const [currentCityId, setCurrentCityId] = useState("");
  const dispatch = useDispatch();

  const [spotTypeFilter, setSpotTypeFilterValue] = useState("");
  const [gridState, setGridState] = useState(initialGridState);
  const cities = useSelector((state) => state.LoginReducer.cities);

  const currentCity = useSelector((state) => state.GoogleMapReducer.city);

  const direction = useSelector((state) => {
    return state.LanguageChangeReducer.direction;
  });
  const spots = useSelector((state) => state.SpotsReducer.allSpots);
  const { location, mapCenter } = useFetchCoordinatesByCity(
    cityFilter,
    cities,
    spots?.pageData
  );

  const hasMounted = useRef(false);
  const containerStyle = {
    height: "250px",
    width: "100%",
  };

  const options = {
    zoomControl: true,
    clickableIcons: false,
    disableDefaultUI: true,
    gestureHandling: "greedy",
  };

  //-------Get More Centers List on Scroll
  const getMoreSpots = () => {
    const updatedGridState = { ...gridState, length: gridState.length + 10 };
    setGridState(updatedGridState);
  };

  const columns = {
    city: "spotInfo.cityId",
    spotType: "spotInfo.spotTypeId",
  };

  const buildFilterQuery = () => {
    let filterQueryExpression = defaultQueryExpression;

    if (cityFilter) {
      filterQueryExpression += ` && ${columns.city} == ${cityFilter}`;
    }

    if (spotTypeFilter) {
      filterQueryExpression += ` && ${columns.spotType} == ${spotTypeFilter}`;
    }

    return filterQueryExpression;
  };

  const onFilterChange = useCallback((key, value) => {
    switch (key) {
      case columns.city:
        setCityFilterValue(value);
        break;
      case columns.spotType:
        setSpotTypeFilterValue(value);
        break;
      default:
        break;
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const filterQueryExpression = buildFilterQuery();
    setGridState({
      ...gridState,
      filterQuery: filterQueryExpression,
    });
    //eslint-disable-next-line
  }, [spotTypeFilter, cityFilter]);

  useEffect(() => {
    setSpotTypes(getSpotTypes(t));
    //eslint-disable-next-line
  }, [i18n.dir()]);

  useEffect(() => {
    const isCityInSelector = cities.find((city) =>
      currentCity.includes(city.name)
    );
    if (isCityInSelector) {
      setCityFilterValue(isCityInSelector?.id);
      setCurrentCityId(isCityInSelector?.id);
    } else {
      setCityFilterValue(defaultCountry);
    }
    //eslint-disable-next-line
  }, [currentCity, cities]);

  useEffect(() => {
    dispatch(authActions.fetchCities(process.env.REACT_APP_Country_Id));
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (hasMounted.current) {
      dispatch(SpotActions.getAllSpots(gridState));
    } else {
      hasMounted.current = true;
    }
    //eslint-disable-next-line
  }, [gridState]);

  useEffect(() => {
    let spotList = [];
    spots?.pageData?.forEach((spot) => {
      const spotObj = {
        latitude: parseFloat(spot.spotInfo.lat),
        longitude: parseFloat(spot.spotInfo.long),
        locationName: spot?.spotInfo?.name,
        locationArabicName: spot?.spotInfo?.arabicName,
        sports: spot?.sportIds || [], // Ensure it defaults to an empty array if undefined
        spotTypeId: spot?.spotInfo?.spotTypeId,
        isPublic: spot?.spotInfo?.spotTypeId,
      };
      spotList.push(spotObj);
    });

    setMainMapLocations(spotList);
    //eslint-disable-next-line
  }, [spots]);

  const handleClearFilters = () => {
    setSpotTypeFilterValue("");
    setCityFilterValue("");
    setGridState({
      ...gridState,
      filterQuery: defaultQueryExpression,
    });
  };

  return (
    <div className="container UserDiv pe-4 ">
      <section className="row py-2">
        <section className="d-flex flex-column flex-sm-row flex-md-row flex-lg-row justify-content-between p-0">
          <div className="col-md-5 col-12">
            <p className="page-heading">{t(translationConstants.spots)}</p>
          </div>
          <div className="col-md-7 col-12 d-md-flex gap-2 justify-content-md-end align-items-center p-0 ">
            <div className="col-md-5 col-12 px-1 py-1 py-md-0">
              <Form.Group className="form-group-register m-0">
                <Form.Select
                  className="custom-select-icon  filter-style"
                  onChange={(e) => {
                    onFilterChange(columns.spotType, e.target.value);
                  }}
                  value={spotTypeFilter}
                  aria-label="Default select example"
                >
                  <option value="">
                    {t(translationConstants.SELECT_SPOT_TYPE)}
                  </option>
                  {spotTypes?.map((spot) => {
                    return (
                      <option key={spot?.id} value={spot.id}>
                        {spot.label}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-5 col-12 px-1 py-1 py-md-0">
              <Form.Group className="form-group-register m-0">
                <Form.Select
                  className="custom-select-icon  filter-style"
                  onChange={(e) => onFilterChange(columns.city, e.target.value)}
                  value={cityFilter}
                  aria-label="Default select example"
                >
                  <option value="">{t(translationConstants.selectcity)}</option>
                  {cities?.map((city) => {
                    return (
                      <option key={city?.id} value={city?.id}>
                        {direction === Directions.rtl
                          ? city.arabicName
                          : city.name}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </div>

            {(cityFilter || spotTypeFilter) && (
              <div className="col-md-2 col-12 justify-content-md-end w-auto py-1 py-md-0">
                <div className="d-flex justify-content-center justify-content-md-end align-items-center mx-1">
                  <div className="cancel-filter" onClick={handleClearFilters}>
                    {t(translationConstants.CLEAR_FILTERS)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="py-2">
          <GoogleMapView
            locations={
              location ? [{ ...location, isShowMark: false }] : mainMapLocations
            }
            options={options}
            containerStyle={containerStyle}
            mapCenter={mapCenter}
            zoom={5}
            cityName={cities.find((city) => city.id == cityFilter)?.name}
          />
        </section>
        
        <section className="mt-5 mb-5 py-2">
          <InfiniteScroll
            dataLength={spots?.pageData?.length ?? 0}
            next={getMoreSpots}
            hasMore={
              spots?.pageData?.length % 10 === 0 &&
              spots?.pageData?.length !== 0
                ? true
                : false
            }
            loader={loadingIndicator(t)}
            endMessage={dataEndMessage(t, spots?.pageData)}
            style={{ overflow: "hidden" }}
            scrollableTarget={"main-container"}
          >
            {spots?.pageData?.map((spot) => (
              <SpotAttributeWrapper key={spot.id} spotData={spot} />
            ))}
          </InfiniteScroll>
        </section>
      </section>
    </div>
  );
}

export default PublicSpot;
